import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, TextInput, panelGet, useAdminGet } from '../kit';
import { Drawer, Modal } from '../email/emailKit';
import {
  CHANGE_TONE, ChangeView, ChipPick, Collapse, DiffView, Dot, Fact, Facts, FieldChips, JsonBox, MODE_LABEL, MOVED_LIVE_LABEL,
  MovedCandidates, NoteLine, Pill, type RecrawlCtx, RetryMsg, RUN_STATUS, S, STEP_STATUS, StatusPill, Tile, UniLink, arr, fmtAgo,
  fmtBytes, fmtDur, fmtIST, fmtMsDur, fmtN, fmtPct, fmtUsd, isLive, labelize, movedDetectionText, movedNotApplied, n0, num, obj,
  queueText, stepTone, str, TONE_DOT, useAction, useDebounced, useLiveGet, usePoll,
} from './shared';
import { ProjectionBox, fullRunApprovalText, projectionLine, useAutoApprove } from './IfApproved';
import { RunStartModal } from './RunStart';

/**
 * One run (GET run {id}): status, the steps as a Gantt with their durations,
 * counts, the guard's reasons, cost, backup and post-load, every step's log
 * (live while the run is going), the request telemetry, the staged changes
 * (GET staged {id, kind, q, page}: new / modified with the changed fields and
 * a before / after, moved — the same technology under a new URL, with the old
 * URL (moved_from) and the match evidence (diff._moved) — re-added, removals),
 * the candidate moves the matcher did not apply (the diff step's
 * counts.stats) and the controls: Approve all,
 * Approve adds + modifications only, Reject with a note, Cancel, Resume.
 * Next to the approve buttons: the website and master counts now → after approval for both
 * scopes (IfApproved.tsx, from GET run's projection); a dry run that is its university's latest
 * results shows the same as "if these were approved", with a Full run shortcut.
 *
 * While the run is queued, running, promoting or post-loading the drawer
 * re-reads it every 4 s and an open step log every 3 s. A failed re-read keeps
 * the last state on screen and polling on (useLiveGet), so an API restart in
 * the middle of a long run does not end the live view.
 */

const COUNT_ORDER: [string, string][] = [
  ['scraped', 'Scraped'], ['errors', 'Error records'], ['mapped', 'Mapped'], ['cleaned', 'After cleanup'],
  ['master_base', 'Master base'], ['new', 'New'], ['modified', 'Modified'],
  ['moved', 'Moved (same id, new URL)'], ['moved_changed', '… with content changes'], ['moved_relaxed', '… on the reference alone'],
  ['unchanged', 'Unchanged'],
  ['readded', 'Re-added'], ['missing', 'Missing'], ['removed_proposed', 'Removals proposed'], ['enriched', 'Enriched'],
  ['enrich_failed', 'Enrich failed'], ['promoted_new', 'New live'], ['promoted_modified', 'Updated live'],
  ['promoted_moved', 'Moved live'],
  ['promoted_readded', 'Re-added live'], ['promoted_removed', 'Removed live'], ['removed_skipped', 'Removals skipped'],
  ['promote_errors', 'Promote errors'],
];
const COUNT_TONE: Record<string, 'green' | 'blue' | 'red' | 'purple' | 'amber' | 'navy' | undefined> = {
  new: 'green', modified: 'blue', moved: 'navy', moved_relaxed: 'amber', removed_proposed: 'red', readded: 'purple', missing: 'amber',
  errors: undefined, enrich_failed: undefined, promote_errors: undefined,
};
const PCT_OF_BASE = ['new', 'modified', 'moved', 'unchanged', 'readded', 'missing', 'removed_proposed'];
/** guard.approval.reasons written by steps.approval_reasons' moved rules. */
const MOVED_REASON = /moved (to new URLs|on the reference number)/i;

// ---------------------------------------------------------------------------
// log tail
// ---------------------------------------------------------------------------
const StepLog: React.FC<{ runId: string; step: string; initial: string[]; live: boolean; label?: string }> =
  ({ runId, step, initial, live, label }) => {
  const [lines, setLines] = useState<string[]>(initial);
  const [meta, setMeta] = useState<{ bytes?: number; modified_at?: string; truncated?: boolean; missing?: boolean }>({});
  const [err, setErr] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [tail, setTail] = useState(200);
  const box = useRef<HTMLPreElement | null>(null);
  const load = async (n = tail) => {
    const { data, error } = await panelGet(S, 'run_log', { id: runId, step, tail: n });
    if (error) { setErr(error); return; }
    setErr('');
    setLines(arr(obj(data).lines).map(str));
    setMeta(obj(data));
    setLoaded(true);
  };
  // The run view's short tail refreshes with the run; once a longer tail was loaded, that one wins.
  useEffect(() => { if (!loaded) setLines(initial); }, [initial.join('\n')]);  // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(() => load(), 3000, live && loaded);
  useEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-grayscale-1100">
        <span>{label || `${labelize(step)} log`}</span>
        {meta.bytes !== undefined && <span>· {fmtBytes(meta.bytes)}{meta.modified_at ? ` · written ${fmtAgo(meta.modified_at)}` : ''}</span>}
        {meta.missing && <span className="text-amber-800">· no log file (yet)</span>}
        {live && loaded && <span className="text-blue-800">· live, every 3 s</span>}
        <span className="flex-1" />
        <Btn small onClick={() => load(tail)}>{loaded ? 'Refresh' : `Load last ${tail} lines`}</Btn>
        {loaded && tail < 2000 && <Btn small onClick={() => { setTail(2000); load(2000); }}>Last 2,000</Btn>}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      <pre ref={box} className="text-[11px] leading-snug font-mono bg-grayscale-1300 text-grayscale-200 rounded-lg p-2.5 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] max-h-72">
        {lines.length ? lines.join('\n') : loaded ? '(the log is empty)' : '(no lines in the run view for this step — load the log)'}
      </pre>
    </div>
  );
};

// ---------------------------------------------------------------------------
// steps Gantt
// ---------------------------------------------------------------------------
const StepsGantt: React.FC<{ run: any; steps: any[]; live: boolean }> = ({ run, steps, live }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const now = Date.now();
  const t = (v: any) => { const d = v ? new Date(v).getTime() : NaN; return Number.isFinite(d) ? d : null; };
  const starts = steps.map(s => t(s.started_at)).filter((x): x is number => x !== null);
  const base = t(run.started_at) ?? (starts.length ? Math.min(...starts) : null);
  const ends = steps.map(s => {
    const st = t(s.started_at);
    const fin = t(s.finished_at);
    if (fin !== null) return fin;
    if (st !== null && num(s.ms) !== null) return st + n0(s.ms);
    return s.status === 'running' && st !== null ? now : st;
  }).filter((x): x is number => x !== null);
  const runEnd = t(run.finished_at) ?? (live ? now : null);
  const end = Math.max(...ends, runEnd ?? 0, base ?? 0);
  const span = base !== null ? Math.max(1, end - base) : 1;
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / span) * 100))}%`;
  if (!steps.length) return <div className="text-sm text-grayscale-1100">No step has started yet.</div>;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 text-[10px] text-grayscale-1100">
        <div className="w-44 shrink-0" />
        <div className="flex-1 relative h-3">
          {[0, 0.25, 0.5, 0.75, 1].map(f => (
            <span key={f} className={`absolute whitespace-nowrap ${f === 0 ? '' : f === 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
              style={{ left: `${f * 100}%` }}>{fmtMsDur(span * f)}</span>
          ))}
        </div>
        <div className="w-28 shrink-0" />
      </div>
      {steps.map((s, i) => {
        const key = `${str(s.step_id)}#${str(s.attempt)}#${i}`;
        const st = t(s.started_at);
        const ms = num(s.ms) ?? (st !== null ? ((t(s.finished_at) ?? (s.status === 'running' ? now : st)) - st) : null);
        const tone = stepTone(s.status);
        const isOpen = !!open[key];
        const running = s.status === 'running';
        const counts = obj(s.counts);
        const http = obj(s.http);
        return (
          <div key={key} className="flex flex-col">
            <button type="button" onClick={() => setOpen(o => ({ ...o, [key]: !isOpen }))}
              className={`flex items-center gap-2 text-left rounded px-1 py-0.5 hover:bg-grayscale-200 ${isOpen ? 'bg-grayscale-200' : ''}`}>
              <div className="w-44 shrink-0 truncate text-[12px]" title={`${str(s.label)} (${str(s.step_id)})`}>
                <span className="text-grayscale-1100 text-[10px] mr-1">{isOpen ? '▾' : '▸'}</span>
                <span className="font-medium text-grayscale-1300">{str(s.label) || str(s.step_id)}</span>
                {n0(s.attempt) > 1 && <span className="text-[10px] text-grayscale-1100"> · try {s.attempt}</span>}
              </div>
              <div className="flex-1 relative h-3.5 bg-grayscale-200 rounded">
                {base !== null && st !== null && (
                  <div className={`absolute top-0 bottom-0 rounded ${running ? 'animate-pulse' : ''}`}
                    style={{ left: pct(st - base), width: `max(3px, ${pct(ms ?? 0)})`, background: TONE_DOT[tone],
                             opacity: s.status === 'skipped' || s.status === 'pending' ? 0.4 : 0.9 }} />
                )}
              </div>
              <div className="w-28 shrink-0 flex items-center justify-end gap-1.5">
                <Dot tone={tone} title={STEP_STATUS[str(s.status)]?.label || str(s.status)} />
                <span className="text-[11.5px] text-grayscale-1200">
                  {s.status === 'skipped' ? 'skipped' : s.status === 'pending' ? 'pending' : `${fmtMsDur(ms)}${running ? '…' : ''}`}
                </span>
              </div>
            </button>
            {isOpen && (
              <div className="ml-4 mr-1 my-1.5 flex flex-col gap-2 rounded-lg border border-grayscale-300 bg-white p-2.5">
                <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
                  <Pill tone={tone}>{STEP_STATUS[str(s.status)]?.label || str(s.status) || '—'}</Pill>
                  <code className="font-mono text-grayscale-1100">{str(s.step_id)}</code>
                  {num(s.rc) !== null && <span>exit code <b>{s.rc}</b></span>}
                  <span>{fmtIST(s.started_at)}{s.finished_at ? ` → ${fmtIST(s.finished_at)}` : ''}</span>
                </div>
                {s.note && <div className="text-[12.5px] text-grayscale-1300 whitespace-pre-line [overflow-wrap:anywhere]">{str(s.note)}</div>}
                {Object.keys(counts).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(counts).filter(([, v]) => typeof v !== 'object' || v === null).map(([k, v]) => (
                      <span key={k} className="rounded-full bg-grayscale-200 px-2 py-px text-[11px]">
                        {labelize(k)} <b className="text-grayscale-1300">{typeof v === 'number' ? v.toLocaleString() : String(v)}</b>
                      </span>
                    ))}
                    {Object.entries(counts).some(([, v]) => v && typeof v === 'object') && (
                      <Collapse title="Nested counts"><JsonBox value={counts} maxH="12rem" /></Collapse>
                    )}
                  </div>
                )}
                {Object.keys(http).length > 0 && (
                  <div className="text-[11.5px] text-grayscale-1200">
                    HTTP: {Object.entries(http).filter(([, v]) => typeof v !== 'object' || v === null).map(([k, v]) => `${labelize(k)} ${typeof v === 'number' ? v.toLocaleString() : String(v)}`).join(' · ')}
                  </div>
                )}
                <StepLog runId={str(run.id)} step={str(s.step_id)} initial={arr(s.log_tail).map(str)} live={running} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// staged changes
// ---------------------------------------------------------------------------
const KIND_ORDER: [string, string][] = [
  ['new', 'New'], ['modified', 'Modified'], ['moved', 'Moved'], ['readded', 'Re-added'], ['removed', 'Removed'], ['missing', 'Missing'],
  ['unchanged', 'Unchanged'],
];
const URL_KEYS = ['university_technology_url', 'university_portal_url'];

/** The expanded view of a staged `moved` row: the evidence (diff._moved), what promote will do, and
 *  the content diff when there is one. The URL pair is shown once, here, not again in the diff. */
const MovedDetail: React.FC<{ r: any }> = ({ r }) => {
  const d = obj(r.diff);
  const mv = obj(d._moved);
  const changed = d._changed === true || (d._changed === undefined && arr(r.changed_fields).length > 0);
  const live = str(mv.live_state);
  const id = str(r.utech_technology_id);
  const urlChange = obj(d.university_technology_url);
  const sim = (v: any) => (num(v) === null ? '—' : n0(v).toFixed(2));
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12.5px] text-grayscale-1300">
        <b>Same technology</b>: {str(mv.by) === 'live_id' ? <>paired by its live id · </> : null}reference <code className="font-mono">{str(mv.ref) || '—'}</code> · title {sim(mv.title_sim)} · text {sim(mv.text_sim)}
        {live && <> · live row {MOVED_LIVE_LABEL[live] || live.replace(/_/g, ' ')}</>}.
        {' '}Keeps <code className="font-mono">{id || 'its UTECH id'}</code>, its links, saved items, share links and page.
      </div>
      {mv.relaxed === true && (
        <div className="text-[12px] rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">
          Accepted on the reference number alone (university override): the title and text similarity are below 0.5.
        </div>
      )}
      <div className="grid gap-1 text-[12px]">
        <div><span className="text-grayscale-1100">Old URL </span>
          <a href={str(urlChange.before || r.moved_from)} target="_blank" rel="noreferrer noopener" className="text-primary-700 hover:underline [overflow-wrap:anywhere]">{str(urlChange.before || r.moved_from) || '—'}</a></div>
        <div><span className="text-grayscale-1100">New URL </span>
          <a href={str(urlChange.after || r.url)} target="_blank" rel="noreferrer noopener" className="text-primary-700 hover:underline [overflow-wrap:anywhere]">{str(urlChange.after || r.url) || '—'}</a></div>
      </div>
      {d.university_portal_url && <ChangeView field="university_portal_url" change={d.university_portal_url} />}
      {str(d._was_status) === 'missing' && (
        <div className="text-[11.5px] text-grayscale-1100">The master had it as missing; it becomes active again.</div>
      )}
      {live === 'at_new' && (
        <div className="text-[11.5px] text-grayscale-1100">The live row is already at the new URL (an earlier promote was interrupted): only the master is re-keyed.</div>
      )}
      {str(mv.by) === 'live_id' && (
        <div className="text-[11.5px] text-grayscale-1100">
          Healed: an earlier move renamed the live row but never re-keyed the master. The pair is made by the live id whatever the
          moved-detection switches and the similarity floor say, because the live table already decided.
        </div>
      )}
      {!changed && live === 'absent' && (
        <div className="text-[12px] text-grayscale-1300">Not live at either URL: re-enriched and inserted at the new URL under {id || 'the master id'}.</div>
      )}
      {!changed && live !== 'absent' && (
        <div className="text-[12px] text-grayscale-1300">URL change only: not re-enriched; the live row's URL is updated in place.</div>
      )}
      {changed && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[12px] text-grayscale-1300">Content changed: re-enriched, then written under the same id (links replaced as for a modification).</div>
          <DiffView diff={d} fields={r.changed_fields} omit={URL_KEYS} />
        </div>
      )}
    </div>
  );
};

const StagedChanges: React.FC<{ runId: string; counts: Record<string, any>; tick: string }> = ({ runId, counts, tick }) => {
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const dq = useDebounced(q.trim());
  useEffect(() => { setPage(1); }, [kind, dq]);
  const params: Record<string, string | number> = { id: runId, page };
  if (kind) params.kind = kind;
  if (dq) params.q = dq;
  const res = useAdminGet<any>(S, 'staged', params, [runId, kind, dq, page, tick]);
  const d = obj(res.data);
  const rows = arr(d.rows);
  const c = { ...counts, ...obj(d.counts) };
  const total = Object.values(c).reduce((a: number, b: any) => a + n0(b), 0);
  const changed = total - n0(c.unchanged);
  const known = KIND_ORDER.map(([k]) => k);
  const options = [
    { key: '', label: 'All', count: total },
    ...KIND_ORDER.filter(([k]) => n0(c[k]) > 0).map(([k, label]) => ({ key: k, label, count: n0(c[k]), tone: CHANGE_TONE[k] })),
    ...Object.keys(c).filter(k => !known.includes(k) && n0(c[k]) > 0).map(k => ({ key: k, label: labelize(k), count: n0(c[k]) })),
  ];
  if (!total && !res.loading) {
    return <div className="text-sm text-grayscale-1100">Nothing staged for this run (staging happens at the diff and stage steps).</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-grayscale-1100">
        <b className="text-grayscale-1300">{changed.toLocaleString()}</b> changed of {total.toLocaleString()} staged records
        ({fmtPct(changed, total)}). Click a row for its before / after.
      </div>
      <ChipPick options={options} value={kind} onChange={setKind} />
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={q} onChange={setQ} placeholder="Search title, URL, old URL or UTECH id…" wide />
        <span className="text-xs text-grayscale-1100">
          {res.loading ? 'Loading…' : `${fmtN(d.total)} match${n0(d.total) === 1 ? '' : 'es'} · page ${fmtN(d.page)} of ${fmtN(d.pages)}`}
        </span>
        <Btn small disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Btn>
        <Btn small disabled={page >= n0(d.pages)} onClick={() => setPage(p => p + 1)}>Next ›</Btn>
      </div>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      <div className="overflow-auto rounded-lg border border-grayscale-300 max-h-[32rem]">
        <table className="w-full text-[12.5px]">
          <thead className="sticky top-0 bg-grayscale-200 text-[11px] text-grayscale-1200">
            <tr>{['Change', 'Title', 'UTECH id', 'Changed fields', 'Promoted'].map(h => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const key = str(r.url) || String(i);
              const isOpen = !!open[key];
              const k = str(r.change_kind);
              return (
                <React.Fragment key={key}>
                  <tr className={`border-t border-grayscale-200 cursor-pointer hover:bg-grayscale-100 ${isOpen ? 'bg-grayscale-100' : ''}`}
                    onClick={() => setOpen(o => ({ ...o, [key]: !isOpen }))}>
                    <td className="px-2 py-1.5 whitespace-nowrap"><Pill tone={CHANGE_TONE[k] || 'grey'}>{k || '—'}</Pill></td>
                    <td className="px-2 py-1.5 max-w-[22rem]">
                      <div className="truncate font-medium text-grayscale-1300" title={str(r.title)}>{str(r.title) || '(no title)'}</div>
                      <a href={str(r.url)} target="_blank" rel="noreferrer noopener" onClick={e => e.stopPropagation()}
                        className="block truncate text-[11px] text-primary-700 hover:underline" title={str(r.url)}>{str(r.url)}</a>
                      {k === 'moved' && str(r.moved_from) && (
                        <div className="truncate text-[11px] text-grayscale-1100" title={`moved from ${str(r.moved_from)}`}>
                          from <a href={str(r.moved_from)} target="_blank" rel="noreferrer noopener" onClick={e => e.stopPropagation()}
                            className="text-grayscale-1100 underline decoration-dotted hover:text-primary-700">{str(r.moved_from)}</a>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[11px]">{str(r.utech_technology_id) || '—'}</td>
                    <td className="px-2 py-1.5">
                      {k === 'moved' && !arr(r.changed_fields).length
                        ? <Pill tone="grey" title="Only the URL changed: no enrichment, the live row is renamed in place">URL only</Pill>
                        : <FieldChips fields={r.changed_fields} max={4} />}
                      {k === 'moved' && obj(obj(r.diff)._moved).relaxed === true && (
                        <Pill tone="amber" className="ml-1" title="Accepted on the reference number alone (university override)">relaxed</Pill>
                      )}
                      {k === 'moved' && str(obj(obj(r.diff)._moved).by) === 'live_id' && (
                        <Pill tone="grey" className="ml-1" title="Paired by the live id: finishes an earlier move whose master half never ran">healed</Pill>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-[11px]">
                      {r.promote_error ? <span className="text-red-700" title={str(r.promote_error)}>error</span>
                        : r.promoted_at ? <span title={fmtIST(r.promoted_at)} className="text-green-700">✓ {fmtAgo(r.promoted_at)}</span> : '—'}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-grayscale-100">
                      <td colSpan={5} className="px-3 py-2">
                        {r.promote_error && <div className="mb-2"><Msg kind="error">{str(r.promote_error)}</Msg></div>}
                        {k === 'moved' ? <MovedDetail r={r} />
                          : k === 'modified' || k === 'readded' || arr(r.changed_fields).length || Object.keys(obj(r.diff)).some(x => !x.startsWith('_'))
                          ? <DiffView diff={r.diff} fields={r.changed_fields} />
                          : <div className="text-xs text-grayscale-1100">
                            {k === 'new' ? 'A new technology: everything is new.' : k === 'removed' ? 'Missing from enough runs in a row: proposed for removal from the live site.'
                              : k === 'missing' ? 'Not in this scrape; the live row stays until it has been missing for enough runs.' : 'No field-level diff.'}
                          </div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!rows.length && !res.loading && (
              <tr><td colSpan={5} className="px-2 py-3 text-center text-grayscale-1100">No staged record matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// the drawer
// ---------------------------------------------------------------------------
const RunDrawer: React.FC<{ id: string; ctx: RecrawlCtx; onClose: () => void }> = ({ id, ctx, onClose }) => {
  const [localTick, setLocalTick] = useState(0);
  const res = useLiveGet<any>('run', { id }, [id, ctx.tick, localTick]);
  const d = obj(res.data);
  const run = obj(d.run);
  const steps = arr(d.steps);
  const staged = obj(d.staged);
  const http = arr(d.http);
  const projection = d.projection && typeof d.projection === 'object' ? d.projection : null;
  const [fullRun, setFullRun] = useState(false);
  const autoApprove = useAutoApprove();
  const live = isLive(run);
  usePoll(res.reload, 4000, live);
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !live && res.data) ctx.reloadAll();
    wasLive.current = live;
  }, [live]);  // eslint-disable-line react-hooks/exhaustive-deps

  const act = useAction(() => { setLocalTick(t => t + 1); ctx.reloadAll(); });
  const [modal, setModal] = useState<'' | 'approve' | 'reject'>('');
  const [scope, setScope] = useState<'all' | 'adds_mods_only'>('all');
  const [note, setNote] = useState('');

  const c = obj(run.counts);
  const g = obj(run.guard);
  const cost = obj(run.cost);
  const backup = obj(run.backup);
  const pl = obj(run.post_load);
  const actions = obj(run.actions);
  const base = num(c.master_base) ?? num(c.cleaned) ?? num(c.mapped);
  const countKeys = useMemo(() => {
    const known = COUNT_ORDER.filter(([k]) => num(c[k]) !== null);
    const extra = Object.keys(c).filter(k => !COUNT_ORDER.some(([x]) => x === k) && num(c[k]) !== null && typeof c[k] !== 'boolean')
      .map(k => [k, labelize(k)] as [string, string]);
    return [...known, ...extra];
  }, [c]);
  const reasons = Array.from(new Set(arr(g.reasons).map(str)));
  const approval = obj(g.approval);
  const probe = obj(g.probe);
  const holds = arr(g.holds);
  const resume = !!actions.resume;
  const status = str(run.status);
  // Moved technologies: the run's counts, the moved approval rules and the diff step's matcher stats.
  const moved = num(c.moved) ?? n0(staged.moved);
  const movedChanged = n0(c.moved_changed);
  const movedRule = [...arr(approval.reasons), ...reasons].map(str).some(x => MOVED_REASON.test(x));
  const diffStep = steps.slice().reverse().find(s => str(s.step_id) === 'diff' && Object.keys(obj(obj(s.counts).stats)).length > 0);
  const mstats = obj(obj(obj(diffStep).counts).stats);
  const movedSkipped = movedNotApplied(mstats);
  const movedOff = str(mstats.moved_detection).startsWith('off');

  const openApprove = (s: 'all' | 'adds_mods_only') => { setScope(s); setNote(''); setModal('approve'); };
  const doApprove = () => {
    setModal('');
    act.post('approve', 'approve', { id, scope, note }, () => (resume ? 'Resumed: queued to carry on from where it stopped.'
      : `Approved (${scope === 'all' ? 'everything' : 'additions and modifications only'}): queued for backup and promote.`));
  };
  const doReject = () => {
    setModal('');
    act.post('reject', 'reject', { id, note }, () => 'Rejected: nothing was written live.');
  };

  const title = run.id !== undefined ? `Run #${str(run.id)} · ${str(run.code)}` : `Run #${id}`;
  const subtitle = [str(run.name), MODE_LABEL[str(run.mode)] || str(run.mode), str(run.trigger) && `trigger ${str(run.trigger)}`,
    str(run.requested_by) && `by ${str(run.requested_by)}`].filter(Boolean).join(' · ');

  return (
    <Drawer title={title} subtitle={subtitle} onClose={onClose}>
      {res.error && <RetryMsg error={res.error} onRetry={res.reload} stale={res.stale} polling={live} />}
      {res.loading && !res.data ? <Loading label={`Loading run #${id}…`} /> : res.data && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={status} />
            {status === 'running' && run.current_step && <span className="text-xs text-grayscale-1200">step <b>{str(run.current_step)}</b></span>}
            {queueText(run) && <span className="text-xs text-grayscale-1200"><b>{queueText(run)}</b> (one run at a time)</span>}
            <UniLink ctx={ctx} code={run.code} name />
            {live && <span className="text-[11px] text-blue-800">refreshing every 4 s</span>}
            <span className="flex-1" />
            <Btn small onClick={() => ctx.goFlow(str(run.code), str(run.id))}>Replay in flow →</Btn>
          </div>
          <div className="text-[12.5px] text-grayscale-1200">{RUN_STATUS[status]?.hint}</div>
          {run.error && <Msg kind={status === 'done' ? 'ok' : 'error'}>{str(run.error)}</Msg>}

          {(actions.approve || actions.reject || actions.cancel) && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex flex-col gap-2">
              {(status === 'awaiting_approval' || status === 'held') && (
                <div className="text-[13px] text-grayscale-1300">
                  {status === 'held' ? 'Held for a person' : 'Waiting for your approval'} —{' '}
                  {approval.reasons && arr(approval.reasons).length ? arr(approval.reasons).map(str).join('; ') : reasons.join('; ') || 'see the guard below'}.
                  {' '}Review the staged changes below first. Approving takes a fresh backup, then writes the live site.
                </div>
              )}
              {resume && <div className="text-[13px] text-grayscale-1300">The worker stopped before the run finished. Resume carries on from the first step that did not finish (nothing is scraped twice).</div>}
              {str(run.resume_blocked) && (
                <div className="text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Resume is not offered: {str(run.resume_blocked)}.
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {actions.approve && !resume && <Btn kind="primary" small disabled={!!act.busy} onClick={() => openApprove('all')}>Approve all</Btn>}
                {actions.approve && !resume && <Btn small disabled={!!act.busy} onClick={() => openApprove('adds_mods_only')}>Approve adds + modifications only</Btn>}
                {resume && <Btn kind="primary" small disabled={!!act.busy} onClick={() => openApprove((str(run.approval_scope) === 'adds_mods_only' ? 'adds_mods_only' : 'all'))}>Resume</Btn>}
                {actions.reject && <Btn kind="danger" small disabled={!!act.busy} onClick={() => { setNote(''); setModal('reject'); }}>Reject…</Btn>}
                {actions.cancel && (
                  <DangerBtn small onConfirm={() => act.post('cancel', 'cancel', { id }, () => (status === 'running' || status === 'promoting'
                    ? 'Cancel requested: the worker stops its current step (a promote in progress is left resumable).' : 'Cancelled.'))}>
                    {status === 'post_load' ? 'Skip the post-load' : 'Cancel run'}
                  </DangerBtn>
                )}
              </div>
              {projection && projection.approvable && !resume && <ProjectionBox p={projection} title="After approval" />}
            </div>
          )}
          {projection && !projection.approvable && (
            <ProjectionBox p={projection} title="If these results were approved"
              note={(
                <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
                  <span>A dry run changes nothing. {fullRunApprovalText(autoApprove, str(run.code))}</span>
                  <Btn small kind="primary" onClick={() => setFullRun(true)}>Start a full run…</Btn>
                </div>
              )} />
          )}
          {fullRun && <RunStartModal ctx={ctx} code={str(run.code)} mode="full" onClose={() => setFullRun(false)} />}
          <NoteLine note={act.note} onClose={() => act.setNote(null)} />

          {modal === 'approve' && (
            <Modal title={resume ? `Resume run #${id}?` : `Approve run #${id}?`} onClose={() => setModal('')}
              footer={<><Btn onClick={() => setModal('')}>Cancel</Btn><Btn kind="primary" onClick={doApprove}>{resume ? 'Resume' : 'Approve'}</Btn></>}>
              {!resume && (
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-start gap-2">
                    <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} className="mt-1" />
                    <span><b>Everything</b>: {fmtN(c.new ?? 0)} new, {fmtN(c.modified ?? 0)} modified,
                      {moved > 0 && <> <b>{fmtN(moved)} moved</b> (same UTECH id, new URL; {fmtN(movedChanged)} with content changes),</>}
                      {' '}{fmtN(c.readded ?? 0)} re-added
                      and <b>{fmtN(c.removed_proposed ?? 0)} removals</b> from the live site.
                      {projectionLine(projection, 'all') && <span className="block text-xs text-grayscale-1200">After: {projectionLine(projection, 'all')}</span>}</span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="radio" checked={scope === 'adds_mods_only'} onChange={() => setScope('adds_mods_only')} className="mt-1" />
                    <span><b>Additions and modifications only</b>: removals are skipped (their live rows stay, their missing streak continues)
                      {moved > 0 ? '; moves are applied too (a move is not a removal).' : '.'}
                      {projectionLine(projection, 'adds_mods_only') && <span className="block text-xs text-grayscale-1200">After: {projectionLine(projection, 'adds_mods_only')}</span>}</span>
                  </label>
                </div>
              )}
              {!resume && movedRule && (
                <div className="text-[12.5px] rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                  Check a few moved pairs (filter <b>Moved</b> in the staged changes). If any is wrong: Reject, turn moved detection off in
                  the university's drawer and run again.
                </div>
              )}
              <p className="text-xs text-grayscale-1100">The worker takes a fresh backup of the corpus first; no backup, no promote. The search layers catch up afterwards.</p>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">Note (optional, kept on the run)
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000}
                  className="rounded-md border-[0.5px] border-primary-300 p-2 text-sm text-grayscale-1300" />
              </label>
            </Modal>
          )}
          {modal === 'reject' && (
            <Modal title={`Reject run #${id}?`} onClose={() => setModal('')}
              footer={<><Btn onClick={() => setModal('')}>Cancel</Btn><Btn kind="danger" onClick={doReject}>Reject</Btn></>}>
              <p>The run is closed as cancelled; nothing it staged is written live. The next run starts from the master as it is.</p>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">Why (kept on the run)
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={1000} autoFocus
                  className="rounded-md border-[0.5px] border-primary-300 p-2 text-sm text-grayscale-1300" />
              </label>
            </Modal>
          )}

          <Facts>
            <Fact label="Created">{fmtIST(run.created_at)}</Fact>
            <Fact label="Started">{fmtIST(run.started_at)}</Fact>
            <Fact label="Finished">{fmtIST(run.finished_at)}</Fact>
            <Fact label="Duration">{fmtDur(run.duration_s)}{live && run.started_at ? '…' : ''}</Fact>
            {run.approved_by && <Fact label={run.approval_scope === 'reject' ? 'Rejected by' : 'Approved by'}>{str(run.approved_by)} · {fmtIST(run.approved_at)}</Fact>}
            {run.approval_scope && <Fact label="Scope">{str(run.approval_scope).replace(/_/g, ' ')}</Fact>}
            {run.approval_note && <Fact label="Note" wide>{str(run.approval_note)}</Fact>}
            {run.unit_name && <Fact label="Unit">{str(run.unit_name)}</Fact>}
            {num(run.pid) !== null && <Fact label="PID">{str(run.pid)}</Fact>}
            {run.run_dir && <Fact label="Run folder">{str(run.run_dir)}</Fact>}
          </Facts>

          {countKeys.length > 0 && (
            <Section title="Counts">
              <div className="flex gap-2 flex-wrap">
                {countKeys.map(([k, label]) => (
                  <Tile key={k} label={label} value={fmtN(c[k])} tone={n0(c[k]) > 0 ? COUNT_TONE[k] : undefined}
                    hint={PCT_OF_BASE.includes(k) && base ? `${fmtPct(c[k], base)} of ${fmtN(base)}` : undefined} />
                ))}
              </div>
            </Section>
          )}

          <Section title="Steps" right={<span className="text-[11px] text-grayscale-1100">click a step for its counts and log</span>}>
            <StepsGantt run={run} steps={steps} live={live} />
          </Section>

          <Section title="Staged changes">
            {/* Re-read when the staged counts move (the diff and stage steps write them) or after an action. */}
            <StagedChanges runId={id} counts={staged} tick={`${ctx.tick}:${localTick}:${status}:${JSON.stringify(staged)}`} />
            {movedSkipped > 0 || num(mstats.moved_would_match) !== null ? (
              <Collapse title={`Possible moves not applied (${fmtN(movedSkipped)})`}>
                <MovedCandidates stats={mstats} />
              </Collapse>
            ) : movedOff ? (
              <div className="text-[11.5px] text-grayscale-1100">Moved detection was {movedDetectionText(mstats.moved_detection)} for this run: a changed URL stays one removal plus one addition.</div>
            ) : null}
          </Section>

          {(reasons.length > 0 || Object.keys(g).length > 0) && (
            <Section title={g.tripped ? 'Guard — tripped' : 'Guard'}>
              {reasons.length > 0 && <div className="flex flex-wrap gap-1">{reasons.map(r => <Pill key={r} tone="amber">{r.replace(/_/g, ' ')}</Pill>)}</div>}
              <Facts>
                {num(g.ratio) !== null && <Fact label="Mapped / master">{str(g.ratio)}{num(g.expected_min_ratio) !== null ? ` (held below ${g.expected_min_ratio})` : ''}</Fact>}
                {arr(g.validate_reasons).length > 0 && <Fact label="Validate" wide>{arr(g.validate_reasons).map(str).join('; ')}</Fact>}
                {arr(probe.reasons).length > 0 && <Fact label="Probe" wide>{arr(probe.reasons).map(str).join(', ')}</Fact>}
                {arr(probe.warnings).length > 0 && <Fact label="Probe warnings" wide>{arr(probe.warnings).map(str).join('; ')}</Fact>}
                {Object.keys(approval).length > 0 && (
                  <Fact label="Approval" wide>{approval.required ? `needed: ${arr(approval.reasons).map(str).join('; ')}` : 'not needed (auto-approved)'}</Fact>
                )}
              </Facts>
              {holds.length > 0 && (
                <div className="text-xs text-grayscale-1200">
                  Holds: {holds.map((h, i) => <span key={i} className="mr-2">{str(obj(h).reason)} ({fmtIST(obj(h).at)})</span>)}
                </div>
              )}
              <Collapse title="Raw guard"><JsonBox value={g} /></Collapse>
            </Section>
          )}

          <div className="grid gap-4 sm:grid-cols-2 items-start">
            <Section title="Cost">
              <Facts cols="grid-cols-2">
                <Fact label="Estimated">{fmtUsd(cost.est_usd)}</Fact>
                <Fact label="Records enriched">{fmtN(cost.enrich_records)}</Fact>
                {num(cost.cache_hits) !== null && <Fact label="Cache hits">{fmtN(cost.cache_hits)}</Fact>}
                {Object.entries(cost).filter(([k, v]) => !['est_usd', 'enrich_records', 'cache_hits'].includes(k) && (typeof v !== 'object' || v === null))
                  .map(([k, v]) => <Fact key={k} label={labelize(k)}>{typeof v === 'number' ? v.toLocaleString() : str(v)}</Fact>)}
              </Facts>
            </Section>
            <Section title="Backup">
              {Object.keys(backup).length ? (
                <Facts cols="grid-cols-2">
                  <Fact label="Path" wide>{str(backup.path) || '—'}</Fact>
                  <Fact label="Rows">{fmtN(backup.rows)}</Fact>
                  <Fact label="Size">{fmtBytes(backup.bytes)}</Fact>
                  <Fact label="Tables">{Array.isArray(backup.tables) ? backup.tables.length : fmtN(backup.tables)}</Fact>
                  <Fact label="Taken">{fmtIST(backup.at)}</Fact>
                  {backup.sha256 && <Fact label="sha256" wide><code className="text-[11px]">{str(backup.sha256)}</code></Fact>}
                </Facts>
              ) : <div className="text-sm text-grayscale-1100">No backup (taken only before a promote).</div>}
            </Section>
          </div>

          {Object.keys(pl).length > 0 && (
            <Section title={`Post-load — ${str(pl.state) || '—'}`}>
              <Facts>
                <Fact label="Steps" wide>{arr(pl.steps).join(', ') || '—'}</Fact>
                <Fact label="Started">{fmtIST(pl.started_at)}</Fact>
                <Fact label="Finished">{fmtIST(pl.finished_at)}</Fact>
                {pl.last_error && <Fact label="Last error" wide>{str(pl.last_error)}</Fact>}
              </Facts>
              <Collapse title="Details"><JsonBox value={pl} /></Collapse>
            </Section>
          )}

          {http.length > 0 && (
            <Section title="Requests (governor telemetry)">
              <div className="overflow-auto rounded-lg border border-grayscale-300">
                <table className="w-full text-[12px]">
                  <thead className="bg-grayscale-200 text-[11px] text-grayscale-1200">
                    <tr>{['Step', 'Requests', 'OK', '429', '403', '5xx', 'Timeouts', 'CF', 'Backoffs', 'avg', 'p95', 'Workers', 'Interval'].map(h =>
                      <th key={h} className="text-left px-2 py-1 whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {http.map((h, i) => (
                      <tr key={i} className="border-t border-grayscale-200">
                        <td className="px-2 py-1">{str(h.step_id) || '—'}</td>
                        <td className="px-2 py-1">{fmtN(h.requests)}</td>
                        <td className="px-2 py-1">{fmtPct(h.ok, h.requests)}</td>
                        <td className={`px-2 py-1 ${n0(h.s429) ? 'text-red-700 font-medium' : ''}`}>{fmtN(h.s429)}</td>
                        <td className={`px-2 py-1 ${n0(h.s403) ? 'text-red-700' : ''}`}>{fmtN(h.s403)}</td>
                        <td className="px-2 py-1">{fmtN(h.s5xx)}</td>
                        <td className="px-2 py-1">{fmtN(n0(h.timeouts) + n0(h.conn_errors))}</td>
                        <td className="px-2 py-1">{fmtN(h.cf_challenges)}</td>
                        <td className="px-2 py-1">{fmtN(h.backoffs)}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{num(h.avg_ms) === null ? '—' : `${Math.round(n0(h.avg_ms))} ms`}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{num(h.p95_ms) === null ? '—' : `${Math.round(n0(h.p95_ms))} ms`}</td>
                        <td className="px-2 py-1">{fmtN(h.max_workers_used)}</td>
                        <td className="px-2 py-1">{num(h.min_interval_used) === null ? '—' : `${h.min_interval_used} s`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Section title="Worker log">
            <StepLog runId={id} step="worker" initial={arr(run.worker_log_tail).map(str)} live={live} label="Last lines" />
          </Section>

          <div className="flex flex-col gap-2">
            {Object.keys(obj(run.throttle)).length > 0 && <Collapse title="Throttle used by this run"><JsonBox value={run.throttle} /></Collapse>}
            {Object.keys(obj(run.settings_snapshot)).length > 0 && <Collapse title="Settings snapshot (frozen when the run was requested)"><JsonBox value={run.settings_snapshot} maxH="24rem" /></Collapse>}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default RunDrawer;
